// Pure math for the Utilization Planner (Judy's design, docs: Capacity-Planner-Design-Notes).
// Same feed as the Capacity Planner; utilization = assigned hours / the person's own capacity hours,
// so a 0.5 FTE at 20 h/wk is 100%. Rollups sum hours first, which makes them capacity-weighted.
export const monthIdx = (year, month) => year * 12 + (month - 1);

/** Band from the ROUNDED percent so a cell never shows 65% in yellow. `over` is Judy's optional 4th color. */
export function utilBand(demand, capacity) {
  if (!(capacity > 0)) return demand > 0 ? 'over' : 'none';
  const pct = Math.round((demand / capacity) * 100);
  if (pct > 100) return 'over';
  if (pct >= 65) return 'good';
  if (pct >= 50) return 'warn';
  return 'low';
}

export const utilPct = (demand, capacity) => (capacity > 0 ? Math.round((demand / capacity) * 100) : null);

export const SCENARIOS = {
  plan: 'On plan',
  slip3: 'Slips 3 months',
  stall3: 'Stalls 3 months',
  drop: 'Drops out',
};

/**
 * Demand rows [{ projectId, teamId, userId, i, hours }] with per-project scenarios applied.
 * Slip moves the whole project; stall holds work already behind us (before `nowIdx`) and pushes the rest.
 * For a project that has not started the two are identical.
 */
export function scenarioDemand(feed, scenarios = {}, nowIdx = 0) {
  const out = [];
  for (const d of feed.demand) {
    const i = monthIdx(d.year, d.month);
    const s = scenarios[d.projectId] || 'plan';
    if (s === 'drop') continue;
    const moved = s === 'slip3' || (s === 'stall3' && i >= nowIdx) ? 3 : 0;
    out.push({ projectId: d.projectId, teamId: d.teamId, userId: d.userId, i: i + moved, hours: d.hours });
  }
  return out;
}

/**
 * Per-team rows over the feed's months. Team demand counts everything (named + team-level forecast);
 * each person carries only the hours named to them, and `unassigned` is the team-level remainder --
 * so a team forecast before anyone is named still shows up, instead of looking like an idle team.
 */
export function buildUtil(feed, { teamIds = [], includePipeline = true, scenarios = {}, nowIdx = 0, priceAt = null }) {
  const base = monthIdx(feed.months[0].year, feed.months[0].month);
  const n = feed.months.length;
  const zero = () => new Array(n).fill(0);
  const pipeline = new Set(feed.projects.filter((p) => p.isPipeline).map((p) => p.id));
  const teams = new Map(feed.teams.filter((t) => !teamIds.length || teamIds.includes(t.id)).map((t) => [t.id, { team: t, demand: zero(), capacity: zero(), unassigned: zero(), people: [], byProject: new Map() }]));
  const people = new Map();
  for (const p of feed.people) {
    const t = teams.get(p.teamId);
    if (!t) continue;
    const row = { person: p, demand: zero(), capacity: p.capacity.map((c) => c || 0) };
    people.set(p.id, row);
    t.people.push(row);
    row.capacity.forEach((c, k) => { t.capacity[k] += c; });
  }
  for (const raw of scenarioDemand(feed, scenarios, nowIdx)) {
    // priceAt turns hours into revenue at the (post-scenario) month's rate; capacity stays in hours.
    const d = priceAt ? { ...raw, hours: raw.hours * priceAt(raw.projectId, raw.i) } : raw;
    const k = d.i - base;
    const t = teams.get(d.teamId);
    if (!t || k < 0 || k >= n || (!includePipeline && pipeline.has(d.projectId))) continue;
    t.demand[k] += d.hours;
    if (!t.byProject.has(d.projectId)) t.byProject.set(d.projectId, zero());
    t.byProject.get(d.projectId)[k] += d.hours;
    if (d.userId && people.has(d.userId)) people.get(d.userId).demand[k] += d.hours;
    else t.unassigned[k] += d.hours;
  }
  return { base, n, teams: [...teams.values()] };
}

/** Sum arrays into month / quarter / year buckets: [{ label, demand, capacity }]. */
export function bucketize(feed, demand, capacity, grain) {
  const out = [];
  feed.months.forEach((m, k) => {
    const key = grain === 'year' ? `${m.year}` : grain === 'quarter' ? `${m.year} Q${Math.floor((m.month - 1) / 3) + 1}` : `${m.year}-${m.month}`;
    let b = out[out.length - 1];
    if (!b || b.key !== key) {
      const label = grain === 'month' ? new Date(m.year, m.month - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' }) : key;
      b = { key, label, demand: 0, capacity: 0, n: 0 };
      out.push(b);
    }
    b.n += 1;
    b.demand += demand[k] || 0;
    b.capacity += capacity[k] || 0;
  });
  return out;
}

/** Sum of every team's arrays: the portfolio-wide line for the Overview chart and KPI tiles. */
export function totals(grid) {
  const demand = new Array(grid.n).fill(0);
  const capacity = new Array(grid.n).fill(0);
  for (const t of grid.teams) t.demand.forEach((d, k) => { demand[k] += d; capacity[k] += t.capacity[k]; });
  return { demand, capacity };
}

/** Change in utilization points per team vs. the baseline over the same buckets, biggest movers first. */
export function teamDeltas(feed, scenarioGrid, baseGrid, grain) {
  const baseBy = new Map(baseGrid.teams.map((t) => [t.team.id, t]));
  return scenarioGrid.teams.map((t) => {
    const b = baseBy.get(t.team.id);
    const now = bucketize(feed, t.demand, t.capacity, grain);
    const was = bucketize(feed, b.demand, b.capacity, grain);
    return {
      team: t.team,
      buckets: now.map((x, i) => ({ label: x.label, pct: utilPct(x.demand, x.capacity), was: utilPct(was[i].demand, was[i].capacity) })),
    };
  });
}

/** Saved scenarios share the PlannerView table with Capacity Planner views; this tells them apart. */
export const serializeScenario = (scenarios) => JSON.stringify({ kind: 'scenario', v: 1, scenarios });
export function isScenarioView(config) {
  try { return JSON.parse(config)?.kind === 'scenario'; } catch { return false; }
}
export const deserializeScenario = (config) => JSON.parse(config).scenarios || {};
